@if (items().length < 2) {
  <div class="flex h-full w-full flex-col items-center justify-center gap-2.5 p-5">
    <div
      class="border-default bg-page text-text-tertiary flex h-11 w-11 items-center justify-center rounded-lg border">
      <i [class]="ICONS.chartBar" class="text-xl"></i>
    </div>
    <p class="text-text-primary text-sm font-semibold">
      {{ 'features.kpi_comparison.title' | translate }}
    </p>
    <p class="text-text-tertiary max-w-[380px] text-center text-xs">
      {{ 'features.kpi_comparison.hint' | translate }}
    </p>
  </div>
} @else {
  <div class="border-default flex shrink-0 items-center gap-2 border-b px-2.5 py-1.5">
    <span class="text-text-secondary font-mono text-[11.5px] whitespace-nowrap">
      {{
        'features.kpi_comparison.summary'
          | translate: { items: items().length, kpis: kpiCount() }
      }}
    </span>
    <span class="bg-weak h-[18px] w-px shrink-0"></span>

    <app-button
      #referenceTrigger
      class="shrink-0"
      type="cancel"
      size="sm"
      [icon]="ICONS.crosshair"
      [label]="
        reference()
          ? ('features.kpi_comparison.reference.short' | translate) +
            ' · ' +
            reference()?.label
          : ('features.kpi_comparison.reference.label' | translate)
      "
      [isForDropdown]="true"
      [active]="reference() !== null"
      (clickEvent)="openMenu(referenceTrigger)" />

    <div class="gx-segmented shrink-0">
      @for (option of MODES; track option.id) {
        <button
          type="button"
          class="seg"
          [class.is-active]="mode() === option.id"
          (click)="setMode(option.id)">
          {{ option.labelKey | translate }}
        </button>
      }
    </div>

    <div class="min-w-2 flex-1"></div>

    @if (focusId(); as focused) {
      <app-button
        class="shrink-0"
        type="cancel"
        size="sm"
        [icon]="ICONS.x"
        [label]="'features.kpi_comparison.isolated' | translate"
        [active]="true"
        (clickEvent)="toggleFocus(focused)" />
    }

    <app-button
      class="shrink-0"
      type="icon"
      [icon]="ICONS.x"
      [ariaLabel]="'features.kpi_comparison.close' | translate"
      (clickEvent)="closed.emit()" />
  </div>

  <div class="min-h-0 flex-1 overflow-auto">
    <div class="grid w-max min-w-full" [style.grid-template-columns]="gridTemplate()">
      <div
        class="bg-surface border-default sticky top-0 left-0 z-30 flex min-h-[46px] items-center border-r border-b px-3">
        <span
          class="text-text-tertiary font-mono text-[10px] font-semibold tracking-[0.06em]">
          {{ 'features.kpi_comparison.indicator' | translate }}
        </span>
      </div>

      @for (item of items(); track item.id) {
        <!-- A div rather than the focus button itself: the plan picker is interactive and
             cannot nest inside a button. -->
        <div
          class="border-l-weak duration-fast sticky top-0 z-20 flex min-h-[46px] flex-col justify-center gap-[3px] border-b border-l px-2.5 py-1.5 transition-opacity ease-out"
          [class]="[
            item.id === referenceId()
              ? 'bg-[color-mix(in_oklch,var(--accent-interactive)_70%,var(--bg-surface))]'
              : 'bg-surface',
            item.id === focusId() ? 'border-b-accent-main' : 'border-b-default',
            isDimmed(item.id) ? 'opacity-40' : ''
          ]"
          [attr.title]="item.label">
          <button
            type="button"
            class="flex min-w-0 cursor-pointer flex-col gap-[3px] text-left"
            (click)="toggleFocus(item.id)">
            <span class="flex min-w-0 items-center gap-1.5">
              <span
                class="inline-block h-2 w-2 shrink-0 rounded-[2px]"
                [style.background]="item.color"></span>
              <span class="truncate font-mono text-[11.5px] font-semibold">{{
                item.label
              }}</span>
            </span>
            @if (item.id === referenceId()) {
              <span
                class="text-accent-main bg-accent-interactive border-accent-border w-fit rounded-[3px] border px-1 font-mono text-[9px] font-semibold tracking-[0.05em]">
                {{ 'features.kpi_comparison.reference.short' | translate }}
              </span>
            } @else if (item.sublabel) {
              <span class="text-text-tertiary truncate font-mono text-[10px]">
                {{ item.sublabel }}
              </span>
            }
          </button>
          @if ((item.choices?.length ?? 0) > 1) {
            <app-select
              class="[&_.gx-select]:gap-1 [&_.gx-select]:px-1.5 [&_.gx-select]:py-0.5 [&_.gx-select]:font-mono [&_.gx-select]:text-[11px]"
              [items]="item.choices ?? []"
              [value]="item.choiceId ?? null"
              [placeholder]="'features.kpi_comparison.choose' | translate"
              [searchable]="(item.choices?.length ?? 0) > 8"
              (valueChange)="choose(item.id, $event)" />
          }
        </div>
      }

      @for (group of groups(); track group.id) {
        <div
          class="bg-elevated border-weak sticky left-0 col-span-full border-b px-3 pt-[7px] pb-1">
          <span
            class="text-text-tertiary font-mono text-[9.5px] font-semibold tracking-[0.07em]">
            {{ group.labelKey | translate }}
          </span>
        </div>

        @for (row of group.rows; track row.key) {
          <div
            class="bg-elevated border-r-default border-b-weak sticky left-0 z-10 flex h-9 items-center gap-1.5 border-r border-b pr-3"
            [class]="row.depth === 1 ? 'pl-7' : 'pl-3'">
            @if (row.depth === 0) {
              @if (row.direction !== 0) {
                <i
                  [class]="row.direction > 0 ? ICONS.arrowUp : ICONS.arrowDown"
                  class="text-text-tertiary shrink-0 text-[9px]"
                  [attr.aria-label]="
                    (row.direction > 0
                      ? 'features.kpi_comparison.direction.higher'
                      : 'features.kpi_comparison.direction.lower'
                    ) | translate
                  "></i>
              }
              <span class="truncate text-xs font-medium">{{
                row.labelKey | translate
              }}</span>
            } @else {
              <span class="text-text-secondary truncate font-mono text-[11px]">{{
                row.mapKey
              }}</span>
            }
          </div>

          @for (cell of row.cells; track cell.itemId) {
            <div
              #cellEl
              data-testid="kpi-comparison-cell"
              class="border-weak duration-fast flex min-h-9 flex-col justify-center border-b border-l px-2.5 transition-opacity ease-out"
              [class]="[
                cell.isReference
                  ? 'bg-[color-mix(in_oklch,var(--accent-interactive)_55%,transparent)]'
                  : '',
                isDimmed(cell.itemId) ? 'opacity-40' : '',
                cell.detail.length > 0 ? 'hover:bg-interactive cursor-pointer' : ''
              ]"
              [attr.role]="cell.detail.length > 0 ? 'button' : null"
              [attr.tabindex]="cell.detail.length > 0 ? 0 : null"
              [attr.aria-haspopup]="cell.detail.length > 0 ? 'dialog' : null"
              (click)="toggleDetail(row, cell, cellEl)"
              (keydown.enter)="onCellKeydown(row, cell, $event)"
              (keydown.space)="onCellKeydown(row, cell, $event)">
              <span class="flex items-baseline justify-end gap-[3px]">
                <span
                  class="font-mono text-[12.5px] font-semibold tabular-nums"
                  [class]="[
                    cell.tone === 'good' || (!cell.tone && cell.isBest)
                      ? 'text-success-hover'
                      : '',
                    cell.tone === 'bad' || (!cell.tone && cell.isWorst)
                      ? 'text-danger-hover'
                      : '',
                    cell.tone === 'flat' || !cell.present ? 'text-text-tertiary' : ''
                  ]">
                  {{ cell.prefix }}{{ cell.text }}
                </span>
                @if (cell.suffix) {
                  <span class="text-text-tertiary text-[9.5px]">{{ cell.suffix }}</span>
                }
              </span>

              @if (cell.bar > 0) {
                @if (cell.barDiverging) {
                  <span
                    class="bg-page relative mt-1 block h-[3px] overflow-visible rounded-[2px]">
                    <span
                      class="bg-strong absolute -top-px -bottom-px left-1/2 w-px"></span>
                    <span
                      class="absolute top-0 bottom-0 rounded-[2px]"
                      [class]="
                        cell.tone === 'good'
                          ? 'bg-success-hover'
                          : cell.tone === 'bad'
                            ? 'bg-danger-hover'
                            : ''
                      "
                      [style.width.%]="cell.bar * 50"
                      [style.left.%]="cell.barPositive ? 50 : 50 - cell.bar * 50"></span>
                  </span>
                } @else {
                  <span
                    class="bg-page relative mt-1 block h-[3px] overflow-hidden rounded-[2px]">
                    <span
                      class="absolute top-0 bottom-0 left-0 rounded-[2px]"
                      [style.width.%]="cell.bar * 100"
                      [style.background]="cell.color"></span>
                  </span>
                }
              }
            </div>
          }
        }
      }
    </div>
  </div>

  <app-context-menu
    [isVisible]="menuOpen()"
    [x]="menuX()"
    [y]="menuY()"
    [options]="menuOptions()"
    (closeMenu)="closeMenu()" />
}
